import { cognitoConfig } from "./config";

const STORAGE_KEY = "fretboard.auth.tokens";

export interface TokenBundle {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface CognitoUser {
  sub: string;
  email?: string;
  given_name?: string;
  family_name?: string;
}

function decodeJwt<T = unknown>(token: string): T {
  const [, payload] = token.split(".");
  const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(padded));
}

export function loadTokens(): TokenBundle | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TokenBundle;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function saveTokens(t: TokenBundle): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function decodeUser(idToken: string): CognitoUser {
  return decodeJwt<CognitoUser>(idToken);
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

function toBundle(res: TokenResponse, existingRefresh?: string): TokenBundle {
  return {
    idToken: res.id_token,
    accessToken: res.access_token,
    refreshToken: res.refresh_token ?? existingRefresh ?? "",
    expiresAt: Date.now() + (res.expires_in - 30) * 1000,
  };
}

export async function exchangeCodeForTokens(code: string, verifier: string): Promise<TokenBundle> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: cognitoConfig.clientId,
    code,
    redirect_uri: cognitoConfig.redirectUri,
    code_verifier: verifier,
  });
  const res = await fetch(`${cognitoConfig.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status} ${await res.text()}`);
  return toBundle(await res.json());
}

export async function refreshTokens(refreshToken: string): Promise<TokenBundle> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: cognitoConfig.clientId,
    refresh_token: refreshToken,
  });
  const res = await fetch(`${cognitoConfig.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) throw new Error(`Token refresh failed: ${res.status} ${await res.text()}`);
  return toBundle(await res.json(), refreshToken);
}
